import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import {
  QuoteRequestSubmitSchema,
  type QuoteRequestSubmit,
  PROJECT_TYPES,
} from "@corianpark/shared";
import { useSubmitQuote } from "../hooks/useApiQueries.js";
import { SEO } from "../components/SEO.js";
import { NeonEyebrow, neonButtonSx } from "../components/Neon.js";

const PROJECT_TYPE_DESCRIPTIONS: Record<string, string> = {
  site_cut: "Bulk earth removal — cut and fill, levelling pads, foundations.",
  dam_construction: "Earth dam build or extension — including liners, spillways.",
  earthworks_residential: "House pad, driveway, drainage, septic, residential block work.",
  earthworks_commercial: "Commercial site prep — carparks, hardstands, civil pads.",
  mining_subcontract: "Mining subcontract — bulk haul, overburden, rehab.",
  civil_infrastructure: "Roads, bridges, culverts, drainage infrastructure.",
  land_clearing: "Tree removal, stump pulling, scrub clearance, fenceline prep.",
  demolition: "Building or structure demolition + spoil removal.",
  other: "Anything dirt-related that doesn't fit the boxes above.",
};

const STEP1_FIELDS: Array<keyof QuoteRequestSubmit> = [
  "projectType",
  "scaleHint",
  "description",
  "postcode",
];

export function Quote() {
  const [params] = useSearchParams();
  const prefillPostcode = params.get("postcode") ?? "";
  const prefillProjectType = (params.get("projectType") ?? "site_cut") as
    (typeof PROJECT_TYPES)[number];
  const [step, setStep] = useState<0 | 1>(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    trigger,
  } = useForm<QuoteRequestSubmit>({
    resolver: zodResolver(QuoteRequestSubmitSchema),
    defaultValues: {
      projectType: prefillProjectType,
      postcode: prefillPostcode,
    },
  });
  const mutation = useSubmitQuote();
  const projectType = watch("projectType");

  useEffect(() => {
    if (prefillPostcode) setValue("postcode", prefillPostcode);
  }, [prefillPostcode, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    await mutation.mutateAsync(data as unknown as Record<string, unknown>);
    reset();
    setStep(0);
  });

  const onContinue = async () => {
    const ok = await trigger(STEP1_FIELDS);
    if (ok) setStep(1);
  };

  if (mutation.isSuccess) {
    return (
      <>
        <SEO
          title="Quote received"
          description="We've received your quote request. The Corian Park team will route it to the right operator."
          canonical="https://corianpark.au/quote"
        />
        <Box sx={{ textAlign: "center", py: 8, maxWidth: 560, mx: "auto" }} data-testid="quote-success">
          <CheckCircleIcon sx={{ fontSize: 72, color: "primary.main", mb: 2, filter: "drop-shadow(0 0 16px rgba(255,176,0,0.6))" }} />
          <NeonEyebrow glow>RECEIVED</NeonEyebrow>
          <Typography variant="h2" sx={{ fontSize: 40, mt: 1, mb: 2, fontWeight: 900 }}>
            Got it — we're on it.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Thanks — the Corian Park team will route this to the right operator. We typically reach
            out within 1 business day with a routed operator and rough scope.
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Get a quote for earthwork"
        description="Tell us about the job. The Corian Park team routes to the right operator."
        canonical="https://corianpark.au/quote"
      />

      <Box sx={{ mb: 4, maxWidth: 720 }}>
        <NeonEyebrow glow>QUOTE REQUEST</NeonEyebrow>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: 38, md: 56 },
            fontWeight: 900,
            mt: 1,
            letterSpacing: "-0.02em",
            lineHeight: 1.05,
          }}
        >
          Tell us about the job
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          The Corian Park team routes every request to an operator with the right kit in your
          postcode. No spam, no bots — actual humans.
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 720, mb: 4 }}>
        <Stepper activeStep={step} alternativeLabel>
          <Step>
            <StepLabel>The job</StepLabel>
          </Step>
          <Step>
            <StepLabel>Your details</StepLabel>
          </Step>
        </Stepper>
      </Box>

      {mutation.error && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 720 }}>
          Something went wrong sending your request. Try again, or email us directly.
        </Alert>
      )}

      <Box component="form" onSubmit={onSubmit} sx={{ maxWidth: 720 }}>
        {step === 0 && (
          <>
            <NeonEyebrow>ABOUT THE JOB</NeonEyebrow>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                select
                label="Project type"
                value={projectType}
                {...register("projectType")}
                error={!!errors.projectType}
                helperText={
                  errors.projectType?.message ?? PROJECT_TYPE_DESCRIPTIONS[projectType] ?? " "
                }
                fullWidth
              >
                {PROJECT_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t.replaceAll("_", " ")}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Scale hint"
                placeholder="e.g. 200 m³, half day, 2 hectares — anything that helps us scope."
                {...register("scaleHint")}
                fullWidth
              />
              <TextField
                label="Describe the job"
                multiline
                rows={5}
                placeholder="What needs doing? Site access? Timeline? Anything that helps route to the right operator."
                {...register("description")}
                error={!!errors.description}
                helperText={errors.description?.message}
                fullWidth
              />
              <TextField
                label="Postcode"
                {...register("postcode")}
                error={!!errors.postcode}
                helperText={errors.postcode?.message ?? "4 digits — where the work is."}
                slotProps={{ htmlInput: { maxLength: 4, inputMode: "numeric" } }}
                sx={{ maxWidth: 200 }}
              />
            </Stack>

            <Box sx={{ mt: 4, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={onContinue}
                sx={{ ...neonButtonSx, fontSize: 16 }}
              >
                Continue
              </Button>
              <Typography variant="caption" color="text.secondary">
                We'll only ask for your contact details on the next step.
              </Typography>
            </Box>
          </>
        )}

        {step === 1 && (
          <>
            <NeonEyebrow>YOUR DETAILS</NeonEyebrow>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              So we can send you the operator match.
            </Typography>
            <Stack spacing={2} sx={{ mt: 2, mb: 4 }}>
              <TextField
                label="Your name"
                {...register("name")}
                error={!!errors.name}
                helperText={errors.name?.message}
                fullWidth
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  {...register("email")}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  fullWidth
                />
                <TextField
                  label="Phone"
                  {...register("phone")}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  fullWidth
                />
              </Stack>
              <Box sx={{ display: "none" }}>
                <input type="text" tabIndex={-1} autoComplete="off" {...register("honeypot")} aria-hidden />
              </Box>
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Button
                type="button"
                variant="text"
                startIcon={<ArrowBackIcon />}
                onClick={() => setStep(0)}
              >
                Back to job details
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                startIcon={<RequestQuoteIcon />}
                disabled={mutation.isPending}
                sx={{ ...neonButtonSx, fontSize: 16 }}
              >
                {mutation.isPending ? "Sending…" : "Find me the right operator"}
              </Button>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
              <Chip size="small" label="No spam" sx={{ bgcolor: "background.paper" }} />
              <Chip size="small" label="No bots" sx={{ bgcolor: "background.paper" }} />
              <Chip size="small" label="Real human" sx={{ bgcolor: "background.paper" }} />
            </Stack>
          </>
        )}
      </Box>
    </>
  );
}
